import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import ConfirmSheet from '@/components/ConfirmSheet';
import AdminFarmerPostsSheet from '@/components/AdminFarmerPostsSheet';
import SharedAvatar from '@/components/Avatar';
import { Users, Search, Plus, Trash2, Sprout, User as UserIcon, ShieldCheck, MapPin, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_META = {
  farmer: { label: 'Farmer', color: '#0B4F3F', bg: 'rgba(11,79,63,0.10)', border: 'rgba(11,79,63,0.28)' },
  admin: { label: 'Admin', color: '#B45309', bg: 'rgba(180,83,9,0.10)', border: 'rgba(180,83,9,0.28)' },
  org_admin: { label: 'Org Admin', color: '#0B4F3F', bg: 'rgba(11,79,63,0.08)', border: 'rgba(11,79,63,0.22)' },
};

// A tag is only rendered when the role is not the default "user" (vendor).
// Vendors get NO badge — they're the implicit baseline.
function RoleBadge({ role }) {
  const meta = ROLE_META[role];
  if (!meta) return null;
  return (
    <span
      data-testid={`user-role-${role}`}
      className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-semibold tracking-wide"
      style={{
        color: meta.color,
        background: meta.bg,
        border: `1px solid ${meta.border}`,
      }}
    >
      {role === 'farmer' ? <Sprout className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
      {meta.label}
    </span>
  );
}

// Turn an ISO timestamp into a stable "day key" (YYYY-MM-DD in local time) so
// posts across timezones aggregate into the admin's calendar day.
function dayKey(iso) {
  if (!iso) return 'unknown';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Friendly label for a group header: Today / Yesterday / weekday for last 7 days
// / full date otherwise.
function dayLabel(iso) {
  if (!iso) return 'Unknown date';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown date';
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  const diffDays = Math.floor((today.getTime() - d.getTime()) / 86400000);
  if (diffDays > 0 && diffDays < 7) {
    return d.toLocaleDateString('en-IN', { weekday: 'long' });
  }
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function Avatar({ name, avatar }) {
  return <SharedAvatar name={name} src={avatar} size={40} className="ring-1 ring-[#E4E8EF]" />;
}

export default function AdminUsers() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filters live in the URL so we can restore them on browser back from /u/:id
  const search = searchParams.get('q') || '';
  const roleFilter = searchParams.get('role') || 'all';
  const setSearch = useCallback((v) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v) next.set('q', v); else next.delete('q');
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const setRoleFilter = useCallback((v) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v && v !== 'all') next.set('role', v); else next.delete('role');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ phone: '', name: '', role: 'vendor', location: '' });
  const [creating, setCreating] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [postsTarget, setPostsTarget] = useState(null); // { id, name } — farmer to inspect

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter !== 'all') params.set('role', roleFilter);
      if (search.trim()) params.set('search', search.trim());
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      setUsers(data.users || []);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [api, roleFilter, search]);

  useEffect(() => { load(); }, [load]);

  const counts = users.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  // Group by joined day, newest day first, users within a day stay newest-first
  // (the API already returns them sorted by `created_at` DESC).
  const groups = React.useMemo(() => {
    const map = new Map();
    for (const u of users) {
      const key = dayKey(u.created_at);
      if (!map.has(key)) map.set(key, { key, iso: u.created_at, users: [] });
      map.get(key).users.push(u);
    }
    return Array.from(map.values()).sort((a, b) => (b.iso || '').localeCompare(a.iso || ''));
  }, [users]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.phone.trim()) { toast.error('Phone is required'); return; }
    setCreating(true);
    try {
      const { data } = await api.post('/admin/users', {
        phone: form.phone.trim(),
        name: form.name.trim() || undefined,
        role: form.role,
        location: form.location.trim() || undefined,
      });
      toast.success(`${data.user.role === 'farmer' ? 'Farmer' : 'User'} added — ${data.user.user_code}`);
      setForm({ phone: '', name: '', role: 'vendor', location: '' });
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add user');
    } finally {
      setCreating(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/users/${deleteTarget.id}`);
      const wiped = data.deleted?.produce_posts || 0;
      toast.success(`User deleted${wiped ? ` · ${wiped} post${wiped === 1 ? '' : 's'} removed` : ''}`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-users">
      {/* Search + Add row */}
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6675]" />
          <Input
            data-testid="user-search-input"
            placeholder="Search name, phone, or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-11 rounded-full"
          />
        </div>
        <Button
          data-testid="add-user-btn"
          onClick={() => setAddOpen(true)}
          className="h-11 px-4 flex-shrink-0"
        >
          <Plus className="w-4 h-4" /> Add
        </Button>
      </div>

      {/* Role filter chips — vendors are the default "user" and don't need a chip */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
        {[
          { id: 'all', label: `All · ${users.length}` },
          { id: 'farmer', label: `Farmer · ${counts.farmer || 0}` },
          { id: 'admin', label: `Admin · ${counts.admin || 0}` },
        ].map((f) => (
          <button
            key={f.id}
            data-testid={`user-filter-${f.id}`}
            onClick={() => setRoleFilter(f.id)}
            className={`px-3.5 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all border ${
              roleFilter === f.id
                ? 'bg-[#0B4F3F] text-white border-[#0B4F3F]'
                : 'bg-[#F6F8FB] text-[#5B6675] border-[#E4E8EF] hover:text-[#0B1620]'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* User list */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-20 rounded-2xl skeleton" />
          ))}
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#F6F8FB] border border-[#E4E8EF] flex items-center justify-center mb-3">
            <Users className="w-6 h-6 text-[#5B6675]" />
          </div>
          <p className="text-sm font-semibold text-[#0B1620]">No users found</p>
          <p className="text-xs text-[#5B6675] mt-1">Try a different search or filter</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => (
            <section key={g.key} data-testid={`user-group-${g.key}`} className="space-y-2">
              <div className="sticky top-0 z-10 -mx-1 px-1 pb-1 bg-[#F6F8FB]/80 backdrop-blur-md flex items-center gap-2">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#0B1620]">
                  {dayLabel(g.iso)}
                </p>
                <span className="text-[10px] text-[#5B6675] tabular-nums">
                  {g.users.length} joined
                </span>
                <div className="flex-1 h-px bg-[#E4E8EF]" />
              </div>
              <div className="space-y-2">
                {g.users.map((u) => (
                  <div
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    onClick={() => {
                      if (u.is_new) {
                        setUsers(prev => prev.map(x => x.id === u.id ? { ...x, is_new: false } : x));
                      }
                      navigate(`/u/${u.id}`);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/u/${u.id}`); } }}
                    className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-3.5 flex items-start gap-3 hover:border-[#0B4F3F]/25 hover:bg-[#EFF2F7]/50 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B4F3F]/40"
                  >
                    <Avatar name={u.name} avatar={u.avatar} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-[#0B1620] truncate flex items-center gap-1.5">
                            {u.name || <span className="text-[#5B6675] italic">No name yet</span>}
                            {u.is_new && (
                              <span
                                data-testid={`new-user-badge-${u.id}`}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#15803D] text-[#F6F8FB] text-[9px] font-bold uppercase tracking-wider"
                              >
                                <Sparkles className="w-2.5 h-2.5" /> New
                              </span>
                            )}
                          </p>
                          <p className="text-[11px] text-[#5B6675] font-mono tracking-wide">{u.user_code}</p>
                        </div>
                        <RoleBadge role={u.role} />
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#5B6675]">
                        <span className="tabular-nums">{u.phone}</span>
                        {u.location && (
                          <span className="inline-flex items-center gap-0.5">
                            <MapPin className="w-3 h-3" /> {u.location}
                          </span>
                        )}
                        {u.role === 'farmer' && (
                          <button
                            data-testid={`view-posts-btn-${u.id}`}
                            onClick={(e) => { e.stopPropagation(); setPostsTarget(u); }}
                            className="tabular-nums text-[11px] text-[#0B4F3F] hover:underline decoration-dotted underline-offset-2"
                          >
                            <span className="font-semibold">{u.post_count}</span> post{u.post_count === 1 ? '' : 's'}
                          </button>
                        )}
                        {u.is_verified && u.role === 'farmer' && (
                          <span className="text-[#0B4F3F] font-semibold">Verified</span>
                        )}
                      </div>
                    </div>
                    <button
                      data-testid={`delete-user-btn-${u.id}`}
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(u); }}
                      className="flex-shrink-0 w-9 h-9 rounded-full text-[#5B6675] hover:text-[#DC2626] hover:bg-[#DC2626]/8 transition-colors flex items-center justify-center"
                      aria-label="Delete user"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Add User Sheet */}
      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent
          side="bottom"
          data-testid="add-user-sheet"
          className="p-0 rounded-t-3xl overflow-hidden flex flex-col max-h-[92dvh]"
        >
          <div className="flex justify-center pt-3 pb-1" aria-hidden>
            <div className="w-10 h-1 rounded-full bg-[#E4E8EF]" />
          </div>
          <div className="px-6 pt-3 pb-2">
            <SheetHeader className="text-left">
              <SheetTitle className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque'] tracking-tight">
                Add a user
              </SheetTitle>
              <SheetDescription className="text-[13px] text-[#5B6675]">
                They can log in with the phone number using OTP after you add them.
              </SheetDescription>
            </SheetHeader>
          </div>
          <form onSubmit={handleCreate} className="flex-1 overflow-y-auto px-6 pt-4 space-y-4">
            <div>
              <Label className="text-[13px] font-medium text-[#0B1620] mb-1.5 block">User type <span className="text-[#DC2626]">*</span></Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="user-type-vendor"
                  onClick={() => setForm(f => ({ ...f, role: 'vendor' }))}
                  className={`h-14 rounded-2xl border-2 flex flex-col items-start justify-center px-4 gap-0.5 transition-all ${
                    form.role === 'vendor'
                      ? 'border-[#0B4F3F] bg-[#0B4F3F]/5'
                      : 'border-[#E4E8EF] hover:border-[#0B4F3F]/30 bg-[#F6F8FB]'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <UserIcon className="w-3.5 h-3.5 text-[#0B4F3F]" />
                    <span className="text-[13px] font-semibold text-[#0B1620]">User</span>
                  </div>
                  <span className="text-[10.5px] text-[#5B6675]">Browses produce, requests callbacks</span>
                </button>
                <button
                  type="button"
                  data-testid="user-type-farmer"
                  onClick={() => setForm(f => ({ ...f, role: 'farmer' }))}
                  className={`h-14 rounded-2xl border-2 flex flex-col items-start justify-center px-4 gap-0.5 transition-all ${
                    form.role === 'farmer'
                      ? 'border-[#0B4F3F] bg-[#0B4F3F]/5'
                      : 'border-[#E4E8EF] hover:border-[#0B4F3F]/30 bg-[#F6F8FB]'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <Sprout className="w-3.5 h-3.5 text-[#0B4F3F]" />
                    <span className="text-[13px] font-semibold text-[#0B1620]">Farmer + User</span>
                  </div>
                  <span className="text-[10.5px] text-[#5B6675]">Can also post produce (auto-verified)</span>
                </button>
              </div>
            </div>

            <div>
              <Label className="text-[13px] font-medium text-[#0B1620] mb-1.5 block">Phone number <span className="text-[#DC2626]">*</span></Label>
              <Input
                data-testid="new-user-phone"
                type="tel"
                inputMode="tel"
                placeholder="+919876543210"
                value={form.phone}
                onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                className="h-12"
                required
              />
            </div>

            <div>
              <Label className="text-[13px] font-medium text-[#0B1620] mb-1.5 block">Name <span className="text-[#5B6675] font-normal">(optional)</span></Label>
              <Input
                data-testid="new-user-name"
                type="text"
                placeholder="e.g. Rajesh Patel"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                className="h-12"
              />
              <p className="text-[11px] text-[#5B6675] mt-1.5">If left blank, they'll set it on first login.</p>
            </div>

            <div>
              <Label className="text-[13px] font-medium text-[#0B1620] mb-1.5 block">Location <span className="text-[#5B6675] font-normal">(optional)</span></Label>
              <Input
                data-testid="new-user-location"
                type="text"
                placeholder="e.g. Nashik, Maharashtra"
                value={form.location}
                onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))}
                className="h-12"
              />
            </div>
          </form>
          <div className="px-6 pb-8 pt-3 border-t border-[#E4E8EF] bg-[#F6F8FB] space-y-2">
            <button
              data-testid="add-user-submit-btn"
              type="button"
              onClick={handleCreate}
              disabled={creating || !form.phone.trim()}
              className="w-full h-12 rounded-full text-sm font-semibold bg-[#0B4F3F] hover:bg-[#083D31] text-white transition-all disabled:opacity-60 active:scale-[0.98] shadow-[0_8px_20px_-8px_rgba(11,79,63,0.4)]"
            >
              {creating ? 'Adding…' : 'Add user'}
            </button>
            <button
              type="button"
              onClick={() => setAddOpen(false)}
              disabled={creating}
              className="w-full h-12 rounded-full text-sm font-semibold text-[#0B1620] hover:bg-[#EFF2F7] transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete confirmation sheet */}
      <ConfirmSheet
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete this user permanently?"
        description={
          deleteTarget
            ? `${deleteTarget.name || deleteTarget.user_code || 'This user'} will be removed forever.${
                deleteTarget.role === 'farmer' && deleteTarget.post_count
                  ? ` All ${deleteTarget.post_count} of their produce post${deleteTarget.post_count === 1 ? '' : 's'} will also be deleted permanently.`
                  : ' All of their past posts, reviews, and callback history will also be deleted permanently.'
              } This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete permanently"
        cancelLabel="Keep user"
        destructive
        icon={<Trash2 className="w-6 h-6" strokeWidth={2.2} />}
        onConfirm={confirmDelete}
        loading={deleting}
        testId="delete-user"
      />

      {/* Farmer post moderation sheet */}
      <AdminFarmerPostsSheet
        farmer={postsTarget}
        open={!!postsTarget}
        onOpenChange={(o) => !o && setPostsTarget(null)}
        onCountChange={(delta) => {
          if (!postsTarget) return;
          setUsers(prev => prev.map(u => u.id === postsTarget.id
            ? { ...u, post_count: Math.max(0, (u.post_count || 0) + delta) }
            : u));
        }}
      />
    </div>
  );
}
